import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useLinkTo } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyBindQuoteQuery } from '../api/quotesApi';
import { setSelectedQuote } from '../slice/quoteSlice';
import { RootState } from 'src/store/store';
import QuoteList from '../components/QuoteList';
import ActionButtons from '../components/ActionButtons';
import { styles } from './QuoteScreen.styles';
import { URLS } from 'src/constants/urls';
import { setActiveTab } from 'src/slices/UiSlice';
import { TABS_INDEX } from 'src/constants/tabs';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import Toast from 'src/components/Toast/Toast';

const QuoteScreen: React.FC = () => {
  const dispatch = useDispatch();
  const linkTo = useLinkTo();

  const { selectedQuoteId, quotes } = useSelector((state: RootState) => state.quotes);

  const accountType = useSelector((state: RootState) => state.UI.accountType);
  // Filter quotes to only include those with a valid quoteNumber
  const validQuotes = quotes.filter(
    (quote) =>
      quote.totalPremiumWEndorsements?.amount > 0 &&
      quote.quoteNumber !== '' &&
      !quote.linesOfBusiness?.includes('AL'),
  );

  const [triggerBindQuote] = useLazyBindQuoteQuery();
  const [isPurchaseDisabled, setIsPurchaseDisabled] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (quotes.length > 0 && validQuotes.length === 0) {
      linkTo(URLS.QUOTE.SUCCESS);
    }
  }, [quotes, validQuotes]);

  useEffect(() => {
    // Track the event when the quote screen is landed
    trackGTMEvent('quotes-screen-landed', { userEvent: 'User land on quote screen' }, accountType);
  }, []);

  const navigateToInsurance = () => {
    trackGTMEvent(
      'quote-screen-left',
      {
        buttonTitle: 'Back',
      },
      accountType,
    );

    linkTo(URLS.APPLICATION.INSURANCE);
    dispatch(setActiveTab({ activeTab: TABS_INDEX.FINAL_DETAILS }));
  };

  const handleSelect = (id: string) => {
    dispatch(setSelectedQuote(selectedQuoteId === id ? null : id));
  };

  const handlePurchase = async () => {
    trackGTMEvent(
      'quote-purchase-button-pressed',
      {
        buttonTitle: 'Purchase',
      },
      accountType,
    );

    if (!selectedQuoteId) return;

    try {
      setIsPurchaseDisabled(true); // Disable the button during the API call
      const response = await triggerBindQuote({ quoteId: selectedQuoteId }).unwrap();

      if (response.success) {
        linkTo(URLS.QUOTE.SUCCESS);
      } else {
        setToastMessage('Failed to bind the quote. Please try again.');
        setShowErrorToast(true);
      }
    } catch (error) {
      console.error('Error binding quote:', error);
      setToastMessage('An error occurred. Please try again later.');
      setShowErrorToast(true);
    } finally {
      setIsPurchaseDisabled(false); // Re-enable the button
    }
  };

  return (
    <View style={styles.container}>
      <QuoteList
        quotes={validQuotes} // Pass only valid quotes
        selectedQuote={selectedQuoteId}
        onSelectQuote={handleSelect}
      />
      <ActionButtons
        onBack={navigateToInsurance}
        onPurchase={handlePurchase}
        isPurchaseDisabled={!selectedQuoteId || isPurchaseDisabled}
      />
      {showErrorToast && <Toast type="error" text={toastMessage} animate={true} />}
    </View>
  );
};

export default QuoteScreen;
